import React, { useState, useMemo, useEffect } from "react";
import {
  Box,
  Typography,
  Paper,
  Tabs,
  Tab,
  Alert,
} from "@mui/material";
import { LoadingPlanSteps } from "../../CreateLoadingPlanPage";
import { ViewerComponent } from "../../../../components/viewer/ViewerComponent";
import { InfoBox } from "../../../../components/InfoBox";
import { useLoadingPlan } from "../../../../contexts/LoadingPlanContext";
import { ViewCubeComponent } from "../../../../components/viewer/ViewCubeComponent";
import { DataTable, Column } from "../../../../components/ui/DataTable";
import "./Step4.scss";
import { Part } from "../../../../services/api/partsService";

interface PartRow {
  id: number;
  mark: string;
  type: string;
  subType: string;
  qty: number;
  weight: number;
  layer: string;
}

interface Step4Props {
  uploadedFile: File | null;
  setCurrentStep: (step: LoadingPlanSteps) => void;
  currentPlanId: string | null;
  setCurrentPlanId: (id: string | null) => void;
  onRecalculatingChange?: (isRecalculating: boolean) => void;
}

const Step4: React.FC<Step4Props> = ({ onRecalculatingChange }) => {
  const [activeTab, setActiveTab] = useState(0);
  const [allParts, setAllParts] = useState<PartRow[]>([]);
  const [currentTabParts, setCurrentTabParts] = useState<PartRow[]>([]);
  const [planInfo, setPlanInfo] = useState({ utilization: '0%', layers: 0, parts: 0 });

  const { currentLoadingPlan } = useLoadingPlan();

  // Define table columns
  const columns: Column<PartRow>[] = [
    { id: 'id', label: '#', className: 'table-cell-number', width: 60 },
    { id: 'name', label: 'Name', className: 'table-cell-name', minWidth: 100 },
    { id: 'mark', label: 'Mark', className: 'table-cell-mark', minWidth: 100 },
    { id: 'sequenceNumber', label: 'Sequence', className: 'table-cell-sequence', minWidth: 100 },
    { id: 'type', label: 'Type', className: 'table-cell-type', minWidth: 120 },
    { id: 'subType', label: 'Sub Type', className: 'table-cell-sub-type', minWidth: 120 },
    { id: 'qty', label: 'Qty', className: 'table-cell-qty', minWidth: 80, align: 'right' },
    { id: 'weight', label: 'Weight, lbs', className: 'table-cell-weight', minWidth: 120, align: 'right' },
    { id: 'layer', label: 'Layer', className: 'table-cell-layer', minWidth: 100 },
  ];

  // Get parts from nesting output with layer information, merge with includedParts for full info
  const partsMap = useMemo(
    () => new Map((currentLoadingPlan.includedParts || []).map((part: Part) => [`${part.sanitizedName}_${part.mark}`, part])),
    [currentLoadingPlan.includedParts]
  );

  const handleTabChange = (_: any, newValue: number) => {
    setActiveTab(newValue);
  };

  useEffect(() => {
    // Calculate utilization
    const totalWeight = (currentLoadingPlan.includedParts || []).reduce((sum: number, part: any) => sum + (part.weight || 0), 0);
    const trailerCapacity = currentLoadingPlan.trailerMaxWeight || 1;
    const utilization = ((totalWeight / trailerCapacity) * 100).toFixed(1);

    // Calculate layers count from nesting output
    const layersCount = currentLoadingPlan.nestingOutput?.nested_parts
      ? Math.max(...currentLoadingPlan.nestingOutput.nested_parts.map((part: any) => part.assigned_loading_layer || 1))
      : 0;

    // Count parts excluding support objects (dunnage, pallet)
    const partsCount = currentLoadingPlan.nestingOutput?.nested_parts
      ? currentLoadingPlan.nestingOutput.nested_parts.reduce((acc, part: any) => {
        const basePartId = part.part_id.includes('#') ? part.part_id.split('#')[0] : part.part_id;

        if (basePartId === 'pallet') {
          const childItems = part.child_items || [];
          acc += childItems.length;

          return acc;
        }

        if (basePartId !== 'dunnage') {
          acc++;
        }

        return acc;
      }, 0)
      : 0;

    setPlanInfo({ utilization: `${utilization}%`, layers: layersCount, parts: partsCount });
  }, [currentLoadingPlan.nestingOutput, currentLoadingPlan.includedParts, currentLoadingPlan.trailerMaxWeight]);

  useEffect(() => {
    if (currentLoadingPlan.nestingOutput && currentLoadingPlan.nestingOutput.nested_parts) {
      const allPlanRows: PartRow[] = currentLoadingPlan.nestingOutput.nested_parts
        .filter((part: any) => {
          const basePartId = part.part_id.includes('#') ? part.part_id.split('#')[0] : part.part_id;
          return basePartId !== 'dunnage';
        })
        .flatMap((nestingPart: any) => {
          const basePartId = nestingPart.part_id.includes('#') ? nestingPart.part_id.split('#')[0] : nestingPart.part_id;
          const isPallet = basePartId === 'pallet';

          const items: any[] = isPallet ? (nestingPart.child_items || []) : [nestingPart];

          return items.map((item: any) => {
            const partId = item.part_id;
            const databasePart = currentLoadingPlan.includedParts?.find((p: Part) => partId === `${p.sanitizedName}_${p.globalId}`);
            const partInfo = databasePart || partsMap.get(partId);

            return {
              id: 0,
              name: partInfo?.name,
              mark: partInfo?.mark || item.part_id,
              sequenceNumber: partInfo?.sequenceNumber || 0,
              type: partInfo?.type || '-',
              subType: partInfo?.subType || '-',
              qty: 1,
              weight: parseFloat((partInfo?.weight || 0).toFixed(2)),
              layer: `Layer ${nestingPart.assigned_loading_layer}`
            };
          });
        })
        .map((row: PartRow, index: number) => ({ ...row, id: index + 1 }));

      setAllParts(allPlanRows);
    }
  }, [currentLoadingPlan.nestingOutput, currentLoadingPlan.includedParts, partsMap]);

  useEffect(() => {
    setCurrentTabParts(allParts.filter((row) => activeTab === 0 || row.layer === `Layer ${activeTab}`));
  }, [activeTab, allParts]);

  return (
    <div className="step4">
      <Paper className="form-card">
        {/* Step Title */}
        <Typography variant="subtitle1" fontWeight={600} mb={1}>
          Step 4: Review & Publish
        </Typography>
        <Typography variant="body2" color="text.secondary" mb={3}>
          Generated loading plan is ready for review
        </Typography>

        {/* Image Preview */}
        <Box mb={2}>
          <Box className="viewer-container">
            {allParts.length > 0 ? (
              <>
                <ViewerComponent showNavigation={true} onRecalculatingChange={onRecalculatingChange} />
                <Box className="view-cube-wrapper">
                  <ViewCubeComponent />
                </Box>
              </>
            ) : (
              <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100%" }}>
                <Typography variant="body1" color="text.secondary">No parts available for preview.</Typography>
              </Box>
            )}
          </Box>
        </Box>

        {/* Stats */}
        <Box className="stats-row">
          <InfoBox label="Utilization" value={allParts.length > 0 ? planInfo.utilization : '0%'} />
          <InfoBox label="Layers" value={allParts.length > 0 ? planInfo.layers : 0} />
          <InfoBox label="Parts" value={allParts.length > 0 ? planInfo.parts : 0} />
        </Box>

        {/* Validation Alert */}
        <Alert severity="success" className="validation-alert">
          Plan validated. All safety rules and constraints satisfied.
        </Alert>

        {/* Tabs */}
        <Tabs value={activeTab} onChange={handleTabChange} className="tabs-container">
          <Tab label="ALL" />
          {Array.from({ length: planInfo.layers }, (_, i) => (
            <Tab key={i + 1} label={`LAYER ${i + 1}`} />
          ))}
        </Tabs>

        {/* Table */}
        <DataTable
          columns={columns}
          data={currentTabParts}
          size="small"
          maxHeight={400}
          emptyMessage="No parts included. Please go back to Step 1 to add parts."
          className="table-content"
          headerClassName="table-head"
        />
      </Paper>
    </div>
  );
};

export { Step4 };
